import { useContext } from "react";
import { SeoContext } from "@/lib/seo-context";
import { Helmet } from "react-helmet-async";
import { COMPANY } from "@/data/company";

const SITE = COMPANY.url;
const OG_IMAGE = `${SITE}/og-image.png`;

// Organisation éditrice : enrichie automatiquement dès que company.js est complété.
const organizationSchema = () => {
  const org = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE}/#organization`,
    name: COMPANY.brand,
    url: `${SITE}/`,
    logo: { "@type": "ImageObject", url: `${SITE}/logo-512.png`, width: 512, height: 512 },
    image: OG_IMAGE,
    description: "Assurance RC Pro et décennale pour les artisans du bâtiment : prix indicatifs par métier, guides pratiques et demande de devis gratuite.",
    areaServed: "FR",
    knowsAbout: ["Assurance décennale", "Responsabilité civile professionnelle", "Assurance construction", "Artisans du bâtiment"],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      url: `${SITE}/contact`,
      availableLanguage: "French",
      ...(COMPANY.phone ? { telephone: COMPANY.phone } : {}),
      ...(COMPANY.email ? { email: COMPANY.email } : {}),
    },
  };
  if (COMPANY.legalName) org.legalName = COMPANY.legalName;
  if (COMPANY.phone) org.telephone = COMPANY.phone;
  if (COMPANY.email) org.email = COMPANY.email;
  if (COMPANY.address) org.address = { "@type": "PostalAddress", streetAddress: COMPANY.address, addressCountry: "FR" };
  const ids = [];
  if (COMPANY.siren) ids.push({ "@type": "PropertyValue", propertyID: "SIREN", value: COMPANY.siren });
  if (COMPANY.orias) ids.push({ "@type": "PropertyValue", propertyID: "ORIAS", value: COMPANY.orias });
  if (ids.length) org.identifier = ids;
  return org;
};

const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE}/#website`,
  name: COMPANY.brand,
  alternateName: "decennale-artisans.com",
  url: `${SITE}/`,
  inLanguage: "fr-FR",
  publisher: { "@id": `${SITE}/#organization` },
};

export default function Seo({ title, description, path, jsonLd, noindex = false, type = "website" }) {
  const capture = useContext(SeoContext);
  if (capture) Object.assign(capture, { title, description, path, jsonLd, noindex });
  const canonical = `${SITE}${path}`;
  const pageSchema = {
    "@context": "https://schema.org", "@type": "WebPage", "@id": `${canonical}#webpage`,
    url: canonical, name: title, description, inLanguage: "fr-FR",
    isPartOf: { "@id": `${SITE}/#website` },
    publisher: { "@id": `${SITE}/#organization` },
    primaryImageOfPage: { "@type": "ImageObject", url: OG_IMAGE },
  };
  const structuredData = [organizationSchema(), websiteSchema, pageSchema, ...(Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [])];
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={noindex ? "noindex,nofollow" : "index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1"} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={COMPANY.brand} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content="fr_FR" />
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Décennale Artisans — assurance RC Pro et décennale des artisans du bâtiment" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={OG_IMAGE} />
      {!noindex && <script type="application/ld+json">{JSON.stringify(structuredData).replace(/</g, "\\u003c")}</script>}
    </Helmet>
  );
}
